import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Facebook, Instagram, Loader2, Mail, Phone } from "lucide-react";
import XIcon from "@/components/XIcon";
import Turnstile from "@/components/Turnstile";
import PrivacyNote from "@/components/PrivacyNote";
import { Overline, Reveal } from "@/components/Reveal";
import { API, CANDIDATE } from "@/data/campaign";

const inputCls =
  "w-full border border-pine-700/25 bg-paper px-4 py-3.5 text-base text-ink placeholder:text-ink/40 focus:border-terra-500 focus:outline-none";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [tsToken, setTsToken] = useState("");
  const tsReset = useRef(null);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!tsToken) return;
    setSending(true);
    try {
      await axios.post(`${API}/submissions`, { ...form, type: "contact", interests: [], turnstile_token: tsToken });
      toast.success("Message sent.", { description: "Amin reads every message and will reply personally." });
      setForm({ name: "", email: "", phone: "", message: "" });
      tsReset.current?.();
      setTsToken("");
    } catch (e) {
      tsReset.current?.();
      setTsToken("");
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please email aminvoteward11@gmail.com directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-28 lg:pt-32">
      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
        <Reveal>
          <Overline>Contact</Overline>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
            Have a question, concern, or idea for Ward 11?{" "}
            <span className="italic text-terra-500">Reach out directly.</span>
          </h1>
        </Reveal>

        <div className="mt-14 grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="space-y-4">
              <a
                href={`tel:${CANDIDATE.phone.replace(/-/g, "")}`}
                data-testid="contact-phone-card"
                className="group flex items-center gap-5 border border-pine-700/15 bg-white/60 p-6 transition-colors hover:border-terra-500"
              >
                <span className="flex h-12 w-12 items-center justify-center bg-pine-700 text-paper transition-colors group-hover:bg-terra-500">
                  <Phone className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink/55">Call Amin directly</span>
                  <span className="block font-serif text-xl font-semibold tracking-tight">{CANDIDATE.phone}</span>
                </span>
              </a>
              <a
                href={`mailto:${CANDIDATE.email}`}
                data-testid="contact-email-card"
                className="group flex items-center gap-5 border border-pine-700/15 bg-white/60 p-6 transition-colors hover:border-terra-500"
              >
                <span className="flex h-12 w-12 items-center justify-center bg-pine-700 text-paper transition-colors group-hover:bg-terra-500">
                  <Mail className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink/55">Email anytime</span>
                  <span className="block break-all font-serif text-lg font-semibold tracking-tight">{CANDIDATE.email}</span>
                </span>
              </a>
              <div className="border border-pine-700/15 bg-pine-900 p-6 text-paper">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra-400">An Open Door</p>
                <p className="mt-3 font-serif text-lg italic leading-relaxed text-paper/85">
                  No gatekeepers. No waiting for election season. If it matters to you, it matters to this campaign.
                </p>
                <div className="mt-5 flex gap-3">
                  {[
                    { icon: Facebook, id: "facebook", href: "https://facebook.com/Amnanji" },
                    { icon: XIcon, id: "twitter", href: "https://x.com/Amnanji" },
                    { icon: Instagram, id: "instagram", href: "https://instagram.com/Amnanji" },
                  ].map(({ icon: Icon, id, href }) => (
                    <a
                      key={id}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`contact-social-${id}`}
                      aria-label={id}
                      className="flex h-10 w-10 items-center justify-center border border-paper/15 text-paper/70 transition-colors hover:border-terra-400 hover:text-terra-400"
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <form onSubmit={submit} data-testid="contact-form" className="border border-pine-700/15 bg-white/60 p-6 sm:p-10">
              <h2 className="font-serif text-2xl font-medium tracking-tight">Send a message</h2>
              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/60">Full Name *</span>
                  <input required data-testid="contact-name-input" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
                </label>
                <label className="block">
                  <span className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/60">Email *</span>
                  <input required type="email" data-testid="contact-email-input" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/60">Phone</span>
                  <input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="Optional" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/60">Message *</span>
                  <textarea required rows={5} data-testid="contact-message-input" value={form.message} onChange={set("message")} className={inputCls} placeholder="What's on your mind?" />
                </label>
              </div>
              <div className="mt-8">
                <Turnstile action="contact" onToken={setTsToken} resetRef={tsReset} />
              </div>
              <button
                type="submit"
                disabled={sending || !tsToken}
                data-testid="contact-submit-button"
                className="mt-8 flex items-center gap-2 bg-pine-700 px-8 py-4 text-sm font-semibold text-paper transition-colors hover:bg-terra-500 disabled:opacity-60"
              >
                {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                Send Message
              </button>
              <PrivacyNote />
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Contact;
